import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, FlexStyle, Platform,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../constants/Colors';
import { Area, Space } from '../../../../../components/Map/SetFloorsModal/FloorItem';
import isMobile from '../../../../../components/IsMobile';

interface SpaceDropdownProps {
  selectedSpace: Space,
  setSpaceDropdownShown: Dispatch<SetStateAction<boolean>>,
  setSelectedSpace: Dispatch<SetStateAction<Space>>,
  setSelectedLocationId?: Dispatch<SetStateAction<string>>,
  area: Area,
  spaceDropdownShown: boolean,
  closeOtherDropdowns: () => void,
  hideLabel?: boolean,
  listPositionAbsolute?: boolean,
  style?: FlexStyle | FlexStyle[],
}

const SpaceDropdown = ({
  setSpaceDropdownShown, setSelectedLocationId, setSelectedSpace, area, selectedSpace, hideLabel,
  spaceDropdownShown, closeOtherDropdowns, listPositionAbsolute, style,
}: SpaceDropdownProps) => {
  const [value, onChangeText] = useState<string>('');

  return <View
    style={[styles.container, style]}
    testID='filter-assets-space-list'
  >
    <View style={{ flexDirection: 'row' }}>
      <View style={styles.connectorContainer}>
        <View style={styles.floorAreaConnector} />
        <View style={styles.connectorSpot} />
      </View>
      <View style={{ flex: 1 }}>
        {hideLabel ? <View style={{ marginBottom: styles.heading.marginBottom }} />
          : <Text style={styles.heading}>{'Space'}</Text>}
        {spaceDropdownShown ? <View style={styles.dropdownBox}>
          <TextInput
            placeholder='Search'
            style={[{ marginStart: 8 }, !isMobile && { outlineWidth: 0 }]}
            placeholderTextColor={Colors.light.hereworksPrimaryGrey700}
            {...{ value, onChangeText }}
          />
          <TouchableOpacity
            style={{ flex: 1, alignItems: 'flex-end' }}
            onPress={() => setSpaceDropdownShown(false)}
          >
            <FontAwesome
              size={20}
              style={styles.icon}
              name={'angle-up'}
            />
          </TouchableOpacity>
        </View> : <TouchableOpacity
          style={styles.dropdownBox}
          onPress={() => {
            setSpaceDropdownShown(!spaceDropdownShown);
            closeOtherDropdowns();
          }}
        >
          <Text style={[
            styles.dropdownBoxText,
            !selectedSpace && { color: Colors.light.hereworksPrimaryGrey800 },
          ]}>
            {selectedSpace?.Name ?? 'None'}
          </Text>
          <View style={{ alignItems: 'flex-end' }}>
            <FontAwesome
              size={20}
              style={{
                color: Colors.light.hereworksLightBlue,
                marginEnd: 12,
              }}
              name={spaceDropdownShown ? 'angle-up' : 'angle-down'}
            />
          </View>
        </TouchableOpacity>}
      </View>
    </View>
    <View style={styles.dropdownContainer}>
      {spaceDropdownShown && <ScrollView
        nestedScrollEnabled
        indicatorStyle={'black'}
        style={[styles.dropdownView, listPositionAbsolute && styles.absoluteView]}
      >
        {[null, ...Object.values(area?.spaces ?? {}) ?? []].map((space: Space) => {
          const selected: boolean = selectedSpace?.LocationId === space?.LocationId;
          return (!(value?.length > 0)
            || space?.Name.toLowerCase().includes(value?.trim().toLowerCase()))
            && <TouchableOpacity
              key={`space_option${space?.LocationId}`}
              onPress={() => {
                setSelectedSpace(space);
                setSelectedLocationId?.(space?.LocationId ?? area?.LocationId);
                setSpaceDropdownShown(false);
                closeOtherDropdowns();
              }}
              style={selected && styles.itemSelected}
            >
              <Text style={[styles.dropdownItemText, selected && styles.textSelected]}>
                {space?.Name ?? 'ALL SPACES'}
              </Text>
            </TouchableOpacity>;
        })}
      </ScrollView>}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    marginStart: 44,
  },
  dropdownBox: {
    width: '100%',
    height: 40,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  heading: {
    fontSize: 14,
    lineHeight: 18,
    marginTop: 12,
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  dropdownContainer: {
    marginStart: 20,
  },
  dropdownView: {
    zIndex: 5,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    backgroundColor: '#fff',
    marginTop: 4,
    maxHeight: 180,
    alignSelf: 'stretch',
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginStart: 12,
    flex: 1,
  },
  dropdownItemText: {
    marginStart: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
  textSelected: {
    color: '#FFF',
  },
  itemSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  icon: {
    color: Colors.light.hereworksBlue500,
    marginEnd: 12,
  },
  connectorContainer: {
    flexDirection: 'row',
    marginBottom: 17,
    alignSelf: 'stretch',
  },
  floorAreaConnector: {
    width: 16,
    marginBottom: 2,
    alignSelf: 'stretch',
    borderStartWidth: 1,
    borderBottomWidth: 1,
    borderBottomStartRadius: 6,
    borderColor: Colors.light.hereworksBlue100,
  },
  connectorSpot: {
    width: 3,
    height: 6,
    borderTopStartRadius: 3,
    borderBottomStartRadius: 3,
    backgroundColor: Colors.light.hereworksBlue100,
    alignSelf: 'flex-end',
  },
  absoluteView: Platform.OS === 'android' ? {} : {
    position: 'absolute',
    width: '100%',
  },
});

export default SpaceDropdown;
